import React, { useRef } from 'react'
import { Box, ButtonBase, Chip, Popover, ThemeProvider, Tooltip } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { impactOf } from './impact'
import ImpactPanel from './ImpactPanel'
import { NO_SYSTEM_VALUES } from './systemValueTerms'

/**
 * B7 — MUI cuts a chip label with an ellipsis and caps the chip at 100% of its cell. A Usage chip must show
 * its whole label ("13 mod.", "Common structure"), so the label slot is overridden through the theme (a
 * `styleOverrides` rule lands in the label's own class, so it wins over the default without relying on
 * selector specificity) and the chip is not capped. Stable function: the merged theme is built once per outer theme.
 */
const wholeLabelTheme = (outer) => ({
  ...outer,
  components: {
    ...outer.components,
    MuiChip: {
      ...outer.components?.MuiChip,
      styleOverrides: {
        ...outer.components?.MuiChip?.styleOverrides,
        root: { maxWidth: 'none' }, // V6: the chip keeps its normal small size; the 40 px area is its wrapper
        label: { overflow: 'visible', textOverflow: 'clip', whiteSpace: 'nowrap' }
      }
    }
  }
})

/**
 * The text of the chip of a key ("System value", "Common structure" or "{{modules}} mod."); the table reads it to size the
 * Usage column (DC-36). Story 204 (RN6.9): a system value says so instead of counting modules.
 */
export const usageChipLabel = (t_, termKey = '', artifact = {}, systemValues = NO_SYSTEM_VALUES) => {
  const impact = impactOf(termKey, artifact, systemValues)
  if (impact.systemValue) return t_('System value')
  return impact.commonStructure ? t_('Common structure') : t_('{{modules}} mod.', { modules: impact.modules })
}

/**
 * Historia 176 — RN8f (DC-11, S97-S104, S162). The "Usage" chip of a row: "{{modules}} mod." (T44) or
 * "Common structure" (T45); a tooltip that sums up T60 or T19, or with common structure T67 (+ T68 if there are
 * screens), and a popover with the shared impact blocks (`ImpactPanel`, the same as the confirmation dialog).
 * There is no impact panel outside of these two and the confirmation.
 */
const UsageChip = ({ termKey = '', artifact = {}, systemValues = NO_SYSTEM_VALUES, open = false, onOpen = () => {}, onClose = () => {} }) => {
  const { t_ } = useSmartTranslation()
  // The popover hangs from the chip itself; whether it is open is decided by the table (one at a time, RN8f).
  const chipRef = useRef()
  // V9: MUI only places the popover when it opens; unfolding the screens list must re-place it inside the window.
  const popoverAction = useRef()
  const impact = impactOf(termKey, artifact, systemValues)
  const nowhere = impact.screens === 0 && !impact.commonStructure

  // Story 204 (RN6.4): a system value sums up with the global warning.
  const summary = impact.systemValue
    ? <Box>{t_('This value is shown in every screen that uses it. Apply the change globally?')}</Box>
    : nowhere
      ? <Box>{t_('Not found where it is used')}</Box>
      : (
        <Box>
          {impact.commonStructure ? <Box>{t_('Common structure · visible on every screen')}</Box> : null}
          {impact.screens > 0 || !impact.commonStructure
            ? (
              <Box>
                {impact.commonStructure
                  ? t_('Also in {{screens}} screens of {{modules}} modules', { screens: impact.screens, modules: impact.modules })
                  : t_('Affects {{screens}} screens in {{modules}} modules', { screens: impact.screens, modules: impact.modules })}
              </Box>
              )
            : null}
        </Box>
        )

  return (
    <>
      <ThemeProvider theme={wholeLabelTheme}>
        <Tooltip title={summary} describeChild>
          {/* RN6k / V6: the pressable area (>= 40 x 40 px) is this wrapper; the chip inside stays small. */}
          <ButtonBase
            ref={chipRef}
            onClick={onOpen}
            data-testid={`usage-chip-area-${termKey}`}
            sx={{ minHeight: 40, minWidth: 40, borderRadius: 1, justifyContent: 'center' }}
          >
            <Chip
              size='small'
              label={usageChipLabel(t_, termKey, artifact, systemValues)}
            />
          </ButtonBase>
        </Tooltip>
      </ThemeProvider>
      <Popover open={open} anchorEl={chipRef.current} onClose={onClose} action={popoverAction}>
        <Box data-testid={`usage-popover-${termKey}`} sx={{ p: 2 }}>
          <ImpactPanel termKey={termKey} artifact={artifact} systemValues={systemValues} onExpandedChange={() => popoverAction.current?.updatePosition()} />
        </Box>
      </Popover>
    </>
  )
}

export default UsageChip
